// src/components/MeasurementsProbe.jsx
import { useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { useFrameMeasurements } from "../utils/useFrameMeasurements.js";

/**
 * Lives inside <Canvas> and renders nothing. Every frame it projects the
 * frame's measurement points into screen space and writes them onto a shared
 * ref, which MeasurementsGizmo reads to position its 2D overlay.
 *
 * The camera orbits (CameraControls in CameraManager), so this has to
 * re-project continuously — the overlay cannot sit at fixed CSS coordinates.
 */
export default function MeasurementsProbe({ targetRef }) {
  const { showMeasurements, valid, worldZ, axes, corners } =
    useFrameMeasurements();
  const scratch = useRef(new THREE.Vector3());
  const camera = useThree((state) => state.camera);
  const size = useThree((state) => state.size);

  useFrame(() => {
    const target = targetRef.current;
    if (!target) return;

    // No per-frame work at all while the gizmo is off.
    if (!showMeasurements || !valid) {
      if (target.axes.length > 0) {
        target.axes = [];
        target.corners = [];
        target.onUpdate?.();
      }
      return;
    }

    const vec = scratch.current;

    // Project into `out`, reusing its point objects so nothing is allocated
    // per frame once the arrays have settled.
    const project = (out, source) => {
      for (let i = 0; i < source.length; i++) {
        vec.set(source[i].x, source[i].y, worldZ).project(camera);
        const x = (vec.x * 0.5 + 0.5) * size.width;
        const y = (-vec.y * 0.5 + 0.5) * size.height;
        if (out[i]) {
          out[i].x = x;
          out[i].y = y;
        } else {
          out[i] = { x, y };
        }
      }
      return out;
    };

    const previous = target.axes;
    target.axes = axes.map((axis, index) => {
      const reusable =
        previous[index]?.id === axis.id &&
        previous[index].points.length === axis.points.length
          ? previous[index].points
          : [];
      return { id: axis.id, points: project(reusable, axis.points) };
    });

    target.corners = project(
      target.corners?.length === corners.length ? target.corners : [],
      corners
    );

    target.onUpdate?.();
  });

  return null;
}
